import { AlertTriangle, ArrowRightLeft, Users2, Shuffle, Siren, CheckCircle2 } from "lucide-react";
import { KINDS } from "@/lib/constants";

const Stat = ({ label, value, sub, icon: Icon, tone = "text-sky-400", testid }) => (
  <div className="bg-[#0b1329] border border-white/10 rounded-xl p-4" data-testid={testid}>
    <div className="flex items-center justify-between"><span className="font-mono text-[10px] tracking-widest text-slate-500 uppercase">{label}</span><Icon className={`w-4 h-4 ${tone}`} /></div>
    <div className="font-display text-2xl font-bold mt-2">{value}</div>
    {sub && <div className="text-[11px] text-slate-500 mt-0.5">{sub}</div>}
  </div>
);

export const PlanSummary = ({ plan, elements }) => {
  const s = plan.summary;
  return (
    <div className="space-y-6" data-testid="plan-summary">
      <div className="grid grid-cols-2 lg:grid-cols-5 gap-3">
        <Stat label="Efectivos usados" value={s.agents_used} sub={`sin relevos harían falta ${s.agents_without_reuse}`} icon={Users2} testid="plan-agents-used" />
        <Stat label="Traslados" value={s.relocations} sub={`${s.shift_handovers ?? 0} relevos por cambio de turno`} icon={Shuffle} tone="text-emerald-400" testid="plan-relocations" />
        <Stat label="Pico policial" value={s.peak_police_need} sub="agentes simultáneos regulando" icon={Siren} tone="text-red-400" testid="plan-peak-police" />
        <Stat label="Tareas" value={s.total_tasks} sub={`${plan.detours.filter((d) => d.coords?.length).length} desvíos calculados`} icon={ArrowRightLeft} testid="plan-total-tasks" />
        <Stat label="Déficit" value={s.missing_agents} sub={s.deficit_count ? `${s.deficit_count} tareas sin cubrir` : "todo cubierto"}
          icon={s.missing_agents ? AlertTriangle : CheckCircle2} tone={s.missing_agents ? "text-amber-400" : "text-emerald-400"} testid="plan-deficit" />
      </div>

      {plan.deficits.length > 0 && (
        <div className="border border-amber-500/40 bg-amber-500/5 rounded-xl p-4" data-testid="plan-deficits-list">
          <div className="flex items-center gap-2 text-amber-300 font-semibold text-sm mb-2"><AlertTriangle className="w-4 h-4" /> Puestos sin cubrir</div>
          {plan.deficits.map((d) => (
            <div key={d.task_id} className="text-xs text-slate-300 py-1 border-t border-white/5">
              <span className="font-mono text-amber-300">{d.start}–{d.end}</span> · {d.name} · faltan <b>{d.missing}</b> ({d.allowed.join(" / ")})
            </div>
          ))}
        </div>
      )}

      <div>
        <div className="font-mono text-[10px] tracking-widest text-slate-500 uppercase mb-2">Horarios calculados</div>
        <div className="border border-white/10 rounded-xl overflow-hidden" data-testid="plan-timings-table">
          {elements.filter((e) => plan.timings[e.id]).map((e) => {
            const t = plan.timings[e.id];
            return (
              <div key={e.id} className="grid grid-cols-12 gap-2 px-4 py-2 text-xs border-b border-white/5 items-center">
                <span className="col-span-2 font-mono text-[10px]" style={{ color: KINDS[e.kind].color }}>{KINDS[e.kind].short}</span>
                <span className="col-span-5 text-slate-200">{e.name}</span>
                <span className="col-span-3 font-mono text-slate-300">{t.start_hm} – {t.end_hm}</span>
                <span className="col-span-2 text-slate-500">{t.source}</span>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
};

export const DetoursList = ({ detours }) => (
  <div className="grid md:grid-cols-2 gap-3" data-testid="detours-list">
    {detours.length === 0 && <div className="text-sm text-slate-500">No hay cortes con desvío.</div>}
    {detours.map((d) => (
      <div key={d.element_id} className="bg-[#0b1329] border border-white/10 rounded-xl p-4" data-testid={`detour-card-${d.element_id}`}>
        <div className="flex items-center justify-between">
          <div className="font-semibold text-sm">{d.element_name}{d.pending && <span className="ml-2 text-[10px] text-rose-300 font-normal">Propuesta · acepte el corte previo</span>}</div>
          <span className={`text-[10px] px-2 py-0.5 rounded-full border ${d.status === "ok" ? "border-emerald-500/40 text-emerald-300" : d.status === "parcial" ? "border-lime-500/40 text-lime-300" : d.status === "sin_servicio" ? "border-amber-500/40 text-amber-300" : "border-red-500/40 text-red-300"}`}>
            {d.status === "ok" ? "Desvío libre" : d.status === "parcial" ? "Cruza recorrido" : d.status === "sin_servicio" ? "Servicio de rutas no disponible" : "Sin alternativa"}
          </span>
        </div>
        {d.coords?.length ? (
          <>
            <div className="font-mono text-[11px] text-slate-400 mt-1">{d.distance_m} m · ~{d.duration_min} min en vehículo</div>
            <div className="text-xs text-slate-300 mt-2 leading-relaxed">{d.streets.length ? d.streets.join(" → ") : "Vía sin nombre"}</div>
            {d.special_name && <div className="text-[11px] text-purple-300 mt-2 bg-purple-500/10 border border-purple-500/30 rounded px-2 py-1" data-testid={`detour-special-${d.element_id}`}>Usa zona de tránsito especial: <b>{d.special_name}</b> (habilitar provisionalmente)</div>}
            {d.reversed_name && <div className="text-[11px] text-pink-300 mt-2 bg-pink-500/10 border border-pink-500/30 rounded px-2 py-1" data-testid={`detour-reversed-${d.element_id}`}>Circula por calle con sentido invertido: <b>{d.reversed_name}</b> (regulación en entrada y salida)</div>}
            {d.oneway_conflicts?.length > 0 && (
              <div className="text-[11px] text-red-300 mt-2 bg-red-500/10 border border-red-500/40 rounded px-2 py-1.5" data-testid={`detour-oneway-warning-${d.element_id}`}>
                <b>Contra sentido de circulación:</b> {d.oneway_conflicts.map((c) => `${c.name} (${c.length_m} m)`).join(", ")}. Marcado en rojo en el mapa. Valore invertir el sentido provisionalmente con regulación o buscar otro itinerario.
              </div>
            )}
            {d.oneway_checked && !d.oneway_conflicts?.length && <div className="text-[10px] text-teal-300/80 mt-2" data-testid={`detour-oneway-ok-${d.element_id}`}>Sentidos de circulación comprobados: sin tramos a contra dirección.</div>}
            {d.oneway_checked === false && <div className="text-[10px] text-amber-300/80 mt-2">No se pudieron comprobar los sentidos (OpenStreetMap no disponible).</div>}
            {d.status === "parcial" && <div className="text-[11px] text-lime-300/80 mt-2">El desvío atraviesa el recorrido: habilitar paso cuando haya pasado la comitiva.</div>}
          </>
        ) : d.status === "sin_servicio" ? (
          <div className="text-xs text-amber-300 mt-2" data-testid={`detour-service-error-${d.element_id}`}>El servidor de rutas no respondió. Pulse “Recalcular plan” en unos segundos.</div>
        ) : (
          <div className="text-xs text-slate-400 mt-2" data-testid={`detour-note-${d.element_id}`}>{d.note || "No se ha encontrado ruta alternativa."} Marque en el mapa una <b className="text-purple-300">Zona de tránsito especial</b> que pueda habilitarse y recalcule, o valore acceso solo residentes.</div>
        )}
      </div>
    ))}
  </div>
);

export const SignageTable = ({ signage, parking }) => (
  <div className="grid lg:grid-cols-2 gap-6">
    <div>
      <div className="font-mono text-[10px] tracking-widest text-slate-500 uppercase mb-2">Inventario de señalización y material</div>
      <div className="border border-white/10 rounded-xl overflow-hidden" data-testid="signage-table">
        {signage.map((s) => (
          <div key={s.code} className="grid grid-cols-12 gap-2 px-4 py-2.5 border-b border-white/5 text-xs items-start">
            <span className="col-span-2 font-mono text-amber-300">{s.code}</span>
            <span className="col-span-7 text-slate-200">{s.name}<div className="text-[10px] text-slate-500 mt-0.5">{s.where.join(" · ")}</div></span>
            <span className="col-span-3 text-right font-display text-lg font-bold">{s.qty}</span>
          </div>
        ))}
      </div>
    </div>
    <div>
      <div className="font-mono text-[10px] tracking-widest text-slate-500 uppercase mb-2">Reservas de estacionamiento</div>
      <div className="space-y-2" data-testid="parking-list">
        {parking.length === 0 && <div className="text-sm text-slate-500">Sin zonas de retirada de vehículos.</div>}
        {parking.map((p) => (
          <div key={p.element_id} className="bg-[#0b1329] border border-amber-500/30 rounded-xl p-4">
            <div className="font-semibold text-sm">{p.name}</div>
            <div className="font-mono text-[11px] text-slate-400 mt-1">Prohibido {p.from} – {p.to} · perímetro {p.perimeter_m} m</div>
            <div className="text-xs text-slate-300 mt-2">Capacidad estimada: <b>~{p.est_vehicles} vehículos</b>. Señalizar con R-308 y horario con 48 h de antelación; prever grúa municipal 30 min antes.</div>
          </div>
        ))}
      </div>
    </div>
  </div>
);
